import { OpenAPI } from "simplydo/interfaces";

import { API, APIError } from ".";
import SearchParams from "../searchParams";

export const groups = (api: API) => ({
  create(
    name: string,
    success: (data: OpenAPI.POST<"/groups">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest("POST", "/groups", { name }, success, fail);
  },
  get(
    id: string,
    success: (data: OpenAPI.GET<"/groups/{id}">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest("GET", `/groups/${id}`, null, success, fail);
  },
  logView(
    id: string,
    success: (data: OpenAPI.POST<"/groups/{id}/view">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest("POST", `/groups/${id}/view`, null, success, fail);
  },
  update(
    id: string,
    data: OpenAPI.PUT<"/groups/{id}">["requestBody"],
    success: (data: OpenAPI.PUT<"/groups/{id}">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest("PUT", `/groups/${id}`, data, success, fail);
  },
  delete(
    id: string,
    success: (data: OpenAPI.DELETE<"/groups/{id}">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest("DELETE", `/groups/${id}`, null, success, fail);
  },
  join(
    id: string,
    userId: string,
    success: (
      data: OpenAPI.POST<"/groups/{id}/members/{user_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/groups/${id}/members/${userId}`,
      null,
      success,
      fail,
    );
  },
  bulkJoin(
    id: string,
    data: OpenAPI.POST<"/groups/{id}/members">["requestBody"],
    success: (data: OpenAPI.POST<"/groups/{id}/members">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/groups/${id}/members`,
      data,
      success,
      fail,
    );
  },
  leave(
    id: string,
    userId: string,
    success: (
      data: OpenAPI.DELETE<"/groups/{id}/members/{user_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/groups/${id}/members/${userId}`,
      null,
      success,
      fail,
    );
  },
  bulkLeave(
    id: string,
    data: { users: string[] },
    success: (data: OpenAPI.DELETE<"/groups/{id}/members">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/groups/${id}/members`,
      data,
      success,
      fail,
    );
  },
  discover(
    success: (data: OpenAPI.GET<"/groups">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      "/groups",
      null,
      (data: { groups: OpenAPI.GET<"/groups">["response"] }) =>
        success?.(data.groups),
      fail,
    );
  },
  getMembers(
    id: string,
    params: OpenAPI.GET<"/groups/{id}/members">["parameters"]["query"],
    success: (data: OpenAPI.GET<"/groups/{id}/members">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    const queryParams = new SearchParams(params).toString();
    api.authenticatedRequest(
      "GET",
      `/groups/${id}/members?${queryParams}`,
      null,
      success,
      fail,
    );
  },
  getChallenges(
    id: string,
    success: (data: OpenAPI.GET<"/groups/{id}/challenges">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      `/groups/${id}/challenges`,
      null,
      (data: {
        challenges: OpenAPI.GET<"/groups/{id}/challenges">["response"];
      }) => success?.(data.challenges),
      fail,
    );
  },
  getActivityFeed(
    id: string,
    params: OpenAPI.GET<"/groups/{id}/activityfeed">["parameters"]["query"],
    success: (
      data: OpenAPI.GET<"/groups/{id}/activityfeed">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    const queryParams = new SearchParams(params).toString();
    api.authenticatedRequest(
      "GET",
      `/groups/${id}/activityfeed?${queryParams}`,
      null,
      success,
      fail,
    );
  },
  postToActivityFeed(
    id: string,
    data: OpenAPI.POST<"/groups/{id}/activityfeed">["requestBody"],
    success: (
      data: OpenAPI.POST<"/groups/{id}/activityfeed">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/groups/${id}/activityfeed`,
      data,
      success,
      fail,
    );
  },
  updateActivityFeedPost(
    id: string,
    postId: string,
    data: OpenAPI.POST<"/groups/{id}/activityfeed/{post_id}">["requestBody"],
    success: (
      data: OpenAPI.POST<"/groups/{id}/activityfeed/{post_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/groups/${id}/activityfeed/${postId}`,
      data,
      success,
      fail,
    );
  },
  deletePostFromActivityFeed(
    id: string,
    postId: string,
    success: (
      data: OpenAPI.DELETE<"/groups/{id}/activityfeed/{post_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/groups/${id}/activityfeed/${postId}`,
      null,
      success,
      fail,
    );
  },
  getActivity(
    id: string,
    data: { freq: string; recent: string },
    success: (data: OpenAPI.GET<"/groups/{id}/activity">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      `/groups/${id}/activity?freq=${data.freq}&recent=${data.recent}`,
      null,
      success,
      fail,
    );
  },
  getAnalytics(
    id: string,
    params: OpenAPI.GET<"/groups/{id}/activity/analytics">["parameters"]["query"],
    success: (
      data: OpenAPI.GET<"/groups/{id}/activity/analytics">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    const queryParams = new SearchParams(params).toSafeString();
    api.authenticatedRequest(
      "GET",
      `/groups/${id}/activity/analytics${queryParams}`,
      null,
      success,
      fail,
    );
  },
  getAnalyticsBreakdowns(
    id: string,
    params: OpenAPI.GET<"/groups/{id}/activity/analytics/breakdowns">["parameters"]["query"],
    success: (
      data: OpenAPI.GET<"/groups/{id}/activity/analytics/breakdowns">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    const queryParams = new SearchParams(params).toSafeString();
    api.authenticatedRequest(
      "GET",
      `/groups/${id}/activity/analytics/breakdowns${queryParams}`,
      null,
      success,
      fail,
    );
  },
  getBusinesses(
    groupId: string,
    params: OpenAPI.GET<"/groups/{id}/businesses">["parameters"]["query"],
    success: (data: OpenAPI.GET<"/groups/{id}/businesses">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    const queryParams = new SearchParams(params).toSafeString();
    api.authenticatedRequest(
      "GET",
      `/groups/${groupId}/businesses${queryParams}`,
      null,
      success,
      fail,
    );
  },
  follow(
    id: string,
    success: (
      data: OpenAPI.POST<"/groups/{group_id}/followers">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/groups/${id}/followers`,
      null,
      success,
      fail,
    );
  },
  unfollow(
    id: string,
    success: (
      data: OpenAPI.DELETE<"/groups/{group_id}/followers">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/groups/${id}/followers`,
      null,
      success,
      fail,
    );
  },
  followers(
    id: string,
    params: OpenAPI.GET<"/groups/{group_id}/followers">["parameters"]["query"],
    success: (
      data: OpenAPI.GET<"/groups/{group_id}/followers">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    const queryParams = new SearchParams(params).toString();
    api.authenticatedRequest(
      "GET",
      `/groups/${id}/followers?${queryParams}`,
      null,
      success,
      fail,
    );
  },
  getMemberships(
    success: (data: OpenAPI.GET<"/groups/memberships">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest("GET", `/groups/memberships`, null, success, fail);
  },
  omni(
    id: string,
    query: OpenAPI.GET<"/groups/{id}/omni">["parameters"]["query"],
    success: (data: OpenAPI.GET<"/groups/{id}/omni">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    const params = new SearchParams(query);
    api.authenticatedRequest(
      "GET",
      `/groups/${id}/omni${params.toSafeString()}`,
      null,
      success,
      fail,
    );
  },
});
